import { useEffect, useState } from 'react';
import { fetchReplayLaunchControlsStatus } from './replay/launchControls';

export const REPLAY_CHART_VISIBILITY_STORAGE_KEY = 'tt_replay_chart_visibility_v1';
export const REPLAY_CHART_VISIBILITY_EVENT = 'tt:replay-chart-visibility-changed';

function normalizeReplayChartVisibility(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return Number.isFinite(value) ? value !== 0 : true;
  if (typeof value === 'string') {
    const token = value.trim().toLowerCase();
    if (!token) return true;
    if (token === 'false' || token === '0' || token === 'off' || token === 'no') return false;
    return true;
  }
  return true;
}

export function readReplayChartVisibility(): boolean {
  return true;
}

export function writeReplayChartVisibility(next: boolean): void {
  const safe = normalizeReplayChartVisibility(next);

  try {
    window.dispatchEvent(new CustomEvent(REPLAY_CHART_VISIBILITY_EVENT, { detail: safe }));
  } catch {
    // ignore custom event failures
  }
}

export function useReplayChartVisibility() {
  const [enabled, setEnabled] = useState<boolean>(() => readReplayChartVisibility());

  useEffect(() => {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    let cancelled = false;

    const refresh = async () => {
      try {
        const status = await fetchReplayLaunchControlsStatus({ signal: controller?.signal });
        if (!cancelled) {
          setEnabled(status ? status.replayEnabled : true);
        }
      } catch {
        if (!cancelled) {
          setEnabled(true);
        }
      }
    };

    const onChanged = () => {
      void refresh();
    };

    void refresh();
    window.addEventListener(REPLAY_CHART_VISIBILITY_EVENT, onChanged as EventListener);
    return () => {
      cancelled = true;
      controller?.abort();
      window.removeEventListener(REPLAY_CHART_VISIBILITY_EVENT, onChanged as EventListener);
    };
  }, []);

  const updateEnabled = (next: boolean | ((prev: boolean) => boolean)) => {
    const resolved = typeof next === 'function'
      ? (next as (prev: boolean) => boolean)(enabled)
      : next;
    writeReplayChartVisibility(resolved);
    setEnabled(normalizeReplayChartVisibility(resolved));
  };

  return [enabled, updateEnabled] as const;
}
